import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { PanoData } from '@photo-sphere-viewer/core';
import { SphereVideoPlayerComponent } from '../../components/video-players/pano-video-player/pano-video-player.component';
import { FlatVideoPlayerComponent } from '../../components/video-players/flat-video-player/flat-video-player.component';
import { FunctionsService } from 'src/app/shared/functions.service';
import { Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

interface RecordingAngle {
  id?: string;
  angle?: string;
  displayName: string;
  videoUrl: string;
  recordingStartTime: string;
  panoData: PanoData & { fps?: number | null };
  panoType?: string;
}

@Component({
  selector: 'app-recording-view',
  standalone: true,
  imports: [SphereVideoPlayerComponent, FlatVideoPlayerComponent, CommonModule, TranslateModule, RouterModule],
  templateUrl: './recording-viewer.component.html',
  styleUrls: ['./recording-viewer.component.scss'],
})
export class RecordingViewComponent implements OnInit {
  private fns = inject(FunctionsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);

  angles = signal<RecordingAngle[]>([]);
  selectedAngle = signal<RecordingAngle | null>(null);
  lastPlaybackTime = signal(0);
  
  // Stores the Error Code (e.g., 'recording-not-found')
  error = signal<string | null>(null);
  showLogin = signal(false);
  teamId = signal<string | null>(null);
  sharingKey: string | null = null;

  hasData = computed(() => {
    const angleOk = this.selectedAngle();
    return this.angles().length > 0 && angleOk && angleOk.videoUrl;
  });

  get backLinkParams() {
    return this.sharingKey ? { s: this.sharingKey } : {};
  }

  async ngOnInit() {
    const recordingId = this.route.snapshot.paramMap.get('id');
    this.sharingKey = this.route.snapshot.queryParamMap.get('s');

    if (!recordingId) {
      this.error.set('recording-not-found');
      return;
    }

    try {
      const response = await this.loadRecording(recordingId);

      if(response.teamId){
        this.teamId.set(response.teamId);
      }

      this.titleService.setTitle(`${response.name} | ${environment.appName}`);

      if(response.deleted === true) {
        this.error.set('recording-deleted');
      } else if (Array.isArray(response.angles) && response.angles.length > 0) {
        this.processAngles(response.angles);
      } else {
        // Fallback code if array is empty
        this.error.set('recording-not-ready');
      }
    } catch (err: any) {
      console.error('Error fetching recording:', err);
      
      // 1. Extract Code
      // Firebase functions return the error message string directly in err.message
      // Default to 'unkonwn-error' (matching your server typo) if message is missing
      const errorCode = err.message || 'unkonwn-error';
      
      this.error.set(errorCode);

      // 2. Handle Login Button Trigger
      if (errorCode === 'recording-private-login-required') {
        this.showLogin.set(true);
      }
    }
  }

  redirectToLogin() {
    // Navigate to login with a 'redirect' query param pointing back to this page
    const returnUrl = this.router.url;
    this.router.navigate(['/user/login'], { queryParams: { redirect: returnUrl } });
  }

  private async loadRecording(recordingId: string): Promise<any> {
    const payload = {
      recordingId,
      sharingKey: this.sharingKey,
    };

    try {
      return await this.fns.callFunction('getRecordingViewer', payload);
    } catch (err: any) {
      const errorText = `${err?.code ?? ''} ${err?.message ?? ''}`.toLowerCase();
      const isTransient = [
        'deadline-exceeded',
        'unavailable',
        'resource-exhausted',
      ].some(code => errorText.includes(code));

      if (!isTransient) throw err;

      // A callable can time out while its Cloud Function instance is starting.
      // Give that instance a moment to finish warming, then retry once.
      await new Promise(resolve => setTimeout(resolve, 750));
      return this.fns.callFunction('getRecordingViewer', payload);
    }
  }

  private processAngles(data: any[]) {
    const processedAngles: RecordingAngle[] = [];

    data.forEach((item, index) => {
      // Safety Check
      if (!item.videoUrl && !item.url) return;

      const videoUrl = item.videoUrl || item.url;
      const angleName = item.angle || '';
      
      let displayName = angleName || `Cam ${index + 1}`;
      
      const lowerAngle = String(angleName).toLowerCase();
      const lowerId = String(item.id || '').toLowerCase();
      const lowerUrl = String(videoUrl).toLowerCase();

      // "Wide" Detection
      if (lowerAngle.includes('wide') || lowerId.includes('wide') || lowerUrl.includes('wide')) {
        if (!angleName) displayName = 'Wide';
      }

      processedAngles.push({
        ...item,
        videoUrl, 
        displayName
      });
    });

    if (processedAngles.length === 0) {
      this.error.set('recording-not-ready');
      return;
    }

    // Sort: "Wide" first
    processedAngles.sort((a, b) => {
      const isWideA = a.displayName.toLowerCase().includes('wide');
      const isWideB = b.displayName.toLowerCase().includes('wide');
      if (isWideA && !isWideB) return -1;
      if (!isWideA && isWideB) return 1;
      return 0;
    });

    this.angles.set(processedAngles);
    
    // Select first (Wide)
    this.selectAngle(processedAngles[0]);
  }

  selectAngle(angle: RecordingAngle) {
    this.selectedAngle.set(angle);
  }
}
